import { FC } from 'react';
import Link from 'next/link';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { i18n as I18n } from 'utils/i18n';

interface Venue {
  id: number;
  name: string;
}

interface Space {
  id: number;
  venue: Venue;
  clinical: boolean;
  name: string;
  image: {
    thumbnail: string;
  };
}

interface Props {
  space: Space;
  onRemove?: () => null;
  selectSpace?: (space: Space, venue: Venue) => void;
  navigateToSpace: boolean;
}

const FavouriteSpace: FC<Props> = ({
  space,
  onRemove,
  selectSpace,
  navigateToSpace = false,
}) => {
  const router = useRouter();

  const onBookNow = () => {
    if (!selectSpace) {
      return;
    }

    selectSpace(space, space.venue);

    if (navigateToSpace) {
      router.push(`/rooms/${space.id}`);
    }
  };

  const favouriteClasses = classNames({
    favourite: true,
    'favourite--clinical': !!space.clinical,
    'favourite--non-clinical': !space.clinical,
  });

  const clinicalClasses = classNames({
    'clinical-indicator': true,
    'clinical-indicator--clinical': !!space.clinical,
    'clinical-indicator--non-clinical': !space.clinical,
  });

  return (
    <li className={favouriteClasses} data-testid="favouriteSpace">
      <div className="favourite__image">
        {!!space.image.thumbnail && (
          <img src={space.image.thumbnail} alt={space.name} />
        )}
      </div>
      <div className="favourite__inner">
        <div className="favourite__header">
          <div>
            <span className={clinicalClasses} data-testid="clinicalIndicator">
              {space.clinical
                ? I18n.t('shared.clinical')
                : I18n.t('shared.non_clinical')}
            </span>
            <h4 className="favourite__title">
              <Link href={`/rooms/${space.id}`}>
                <a>{space.name}</a>
              </Link>
            </h4>
            <h5 className="favourite__subtitle">
              <Link href={`/properties/${space.venue.id}`}>
                <a>{space.venue.name}</a>
              </Link>
            </h5>
          </div>
          {onRemove && (
            <button
              onClick={onRemove}
              className="favourite__remove"
              data-testid="removeFavouriteButton"
            >
              <ReactSVG src="/images/icons/accounts/remove.svg" />
            </button>
          )}
        </div>

        <div className="favourite__details">
          <Link href={`/rooms/${space.id}`}>
            <a>{I18n.t('favourite.view_room_details')}</a>
          </Link>
          <button
            onClick={onBookNow}
            className={
              selectSpace
                ? 'button button--primary button--small'
                : 'button button--disabled button--small'
            }
            data-testid="onBookNow"
            {...(!selectSpace ? { disabled: true } : {})}
          >
            {I18n.t('shared.book_now')}
          </button>
        </div>
      </div>
    </li>
  );
};

export default FavouriteSpace;
